// src/pages/Orders/components/tabs/KarigarTab.jsx
// ============================================================================
// ENHANCED KARIGAR TAB - Uses both order slice and orderKarigar slice
// ============================================================================
// This tab leverages:
// 1. order.karigarAssignments - Direct from order object
// 2. order.karigarWorkStatus - Summary from order object
// 3. orderKarigar slice - Available karigars, queue, etc.
// 4. NEW order thunks - /orders/admin/order/:orderId/karigar-jobs/*
// ============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  UserGroupIcon,
  ExclamationCircleIcon,
  BriefcaseIcon,
  PlusIcon,
  EyeIcon,
  UserIcon,
  RefreshIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  PencilIcon,
  LightningBoltIcon,
  TruckIcon,
  ExclamationIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";

// Import order-based karigar thunks
import {
  getOrderKarigarJobsThunk,
  getOrderKarigarSummaryThunk,
  checkKarigarReadinessThunk,
  forceCompleteKarigarJobsThunk,
  updateOrderKarigarJobThunk,
  cancelOrderKarigarJobThunk,
  createBulkOrderKarigarJobsThunk,
} from "../../../../store/thunks/orderThunks";

// Import slice actions
import { clearKarigarJobs } from "../../../../store/slices/orderSlice";

// Status constants
const KARIGAR_STATUS_CONFIG = {
  PENDING_ASSIGNMENT: {
    color: "gray",
    label: "Pending Assignment",
    icon: ClockIcon,
  },
  PENDING: { color: "gray", label: "Pending", icon: ClockIcon },
  ASSIGNED: { color: "yellow", label: "Assigned", icon: UserIcon },
  ACCEPTED: { color: "blue", label: "Accepted", icon: CheckCircleIcon },
  REJECTED: { color: "red", label: "Rejected", icon: XCircleIcon },
  IN_PROGRESS: { color: "indigo", label: "In Progress", icon: RefreshIcon },
  ON_HOLD: { color: "orange", label: "On Hold", icon: ExclamationIcon },
  QUALITY_CHECK: { color: "purple", label: "Quality Check", icon: EyeIcon },
  REVISION_REQUIRED: {
    color: "amber",
    label: "Revision Required",
    icon: PencilIcon,
  },
  COMPLETED: { color: "green", label: "Completed", icon: CheckCircleIcon },
  DELIVERED: { color: "emerald", label: "Delivered", icon: TruckIcon },
  CANCELLED: { color: "red", label: "Cancelled", icon: XCircleIcon },
};

const KarigarTab = ({
  order,
  karigarAssignments,
  itemsWithKarigarStatus,
  itemsWithoutKarigar,
  itemsWithKarigar,
  canAssignKarigar,
  allItemsHaveKarigar,
  allKarigarWorkComplete,
  onAssignKarigar,
  onViewQueue,
  formatCurrency,
  formatDate,
  getKarigarStatusColor,
  loading: externalLoading,
}) => {
  const dispatch = useDispatch();

  // Local state for UI only
  const [activeView, setActiveView] = useState("assignments"); // assignments | summary | readiness
  const [selectedJob, setSelectedJob] = useState(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showForceCompleteModal, setShowForceCompleteModal] = useState(false);

  // Get karigar state from Redux store (orders slice)
  const {
    karigarJobs = {
      list: [],
      summary: null,
      readiness: null,
      loading: false,
      error: null,
      itemJobMapping: [],
    },
  } = useSelector((state) => state.orders || {});

  // Combine loading states
  const isLoading = externalLoading || karigarJobs.loading;

  // Load karigar jobs when order changes
  useEffect(() => {
    if (order?.orderId) {
      dispatch(getOrderKarigarJobsThunk(order.orderId));
    }

    // Cleanup on unmount
    return () => {
      dispatch(clearKarigarJobs());
    };
  }, [order?.orderId, dispatch]);

  // Get status config
  const getStatusConfig = (status) => {
    return (
      KARIGAR_STATUS_CONFIG[status] || {
        color: "gray",
        label: status,
        icon: ClockIcon,
      }
    );
  };

  // Handlers using Redux thunks
  const handleRefreshJobs = useCallback(() => {
    if (order?.orderId) {
      dispatch(getOrderKarigarJobsThunk(order.orderId));
    }
  }, [order?.orderId, dispatch]);

  const handleLoadSummary = useCallback(() => {
    if (order?.orderId) {
      dispatch(getOrderKarigarSummaryThunk(order.orderId));
      setActiveView("summary");
    }
  }, [order?.orderId, dispatch]);

  const handleCheckReadiness = useCallback(() => {
    if (order?.orderId) {
      dispatch(checkKarigarReadinessThunk(order.orderId));
      setActiveView("readiness");
    }
  }, [order?.orderId, dispatch]);

  const handleForceCompleteJobs = useCallback(
    async (completionData) => {
      if (!order?.orderId) return;

      try {
        await dispatch(
          forceCompleteKarigarJobsThunk({
            orderId: order.orderId,
            completionData,
          })
        ).unwrap();

        setShowForceCompleteModal(false);
        // Refresh jobs after force complete
        dispatch(getOrderKarigarJobsThunk(order.orderId));

        // Refresh readiness if we were on that tab
        if (activeView === "readiness") {
          dispatch(checkKarigarReadinessThunk(order.orderId));
        }
      } catch (error) {
        console.error("Force complete failed:", error);
      }
    },
    [order?.orderId, dispatch, activeView]
  );

  const handleBulkCreateJobs = useCallback(
    async (bulkData) => {
      if (!order?.orderId) return;

      try {
        await dispatch(
          createBulkOrderKarigarJobsThunk({
            orderId: order.orderId,
            bulkData,
          })
        ).unwrap();

        setShowBulkAssignModal(false);
        // Refresh jobs after bulk create
        dispatch(getOrderKarigarJobsThunk(order.orderId));
      } catch (error) {
        console.error("Bulk create failed:", error);
      }
    },
    [order?.orderId, dispatch]
  );

  const handleUpdateJobStatus = useCallback(
    async (jobId, updateData) => {
      if (!order?.orderId) return;

      try {
        await dispatch(
          updateOrderKarigarJobThunk({
            orderId: order.orderId,
            jobId,
            updateData,
          })
        ).unwrap();

        // Refresh jobs after update
        dispatch(getOrderKarigarJobsThunk(order.orderId));
      } catch (error) {
        console.error("Update job failed:", error);
      }
    },
    [order?.orderId, dispatch]
  );

  const handleCancelJob = useCallback(
    async (jobId, reason) => {
      if (!order?.orderId) return;

      if (!window.confirm("Are you sure you want to cancel this job?")) return;

      try {
        await dispatch(
          cancelOrderKarigarJobThunk({
            orderId: order.orderId,
            jobId,
            reason: reason || "Cancelled by admin",
          })
        ).unwrap();

        // Refresh jobs after cancel
        dispatch(getOrderKarigarJobsThunk(order.orderId));
      } catch (error) {
        console.error("Cancel job failed:", error);
      }
    },
    [order?.orderId, dispatch]
  );

  if (!order?.items?.length) {
    return (
      <div className="text-center py-8">
        <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No items in this order</p>
      </div>
    );
  }

  // Calculate stats from order data
  const orderKarigarStatus = order.karigarWorkStatus || {};
  const totalItems = order.items?.length || 0;
  const assignedCount =
    orderKarigarStatus.assignedItems || karigarAssignments?.length || 0;
  const inProgressCount = orderKarigarStatus.inProgressItems || 0;
  const completedCount = orderKarigarStatus.completedItems || 0;
  const pendingCount = totalItems - assignedCount;

  return (
    <div className="space-y-6">
      {/* Header with View Toggle & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveView("assignments")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeView === "assignments"
                ? "bg-white text-primary shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <UserGroupIcon className="w-4 h-4 inline mr-1" />
            Assignments
          </button>
          <button
            onClick={handleLoadSummary}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeView === "summary"
                ? "bg-white text-primary shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <ChartBarIcon className="w-4 h-4 inline mr-1" />
            Summary
          </button>
          <button
            onClick={handleCheckReadiness}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeView === "readiness"
                ? "bg-white text-primary shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <TruckIcon className="w-4 h-4 inline mr-1" />
            Readiness
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleRefreshJobs}
            disabled={karigarJobs.loading}
          >
            <RefreshIcon
              className={`w-4 h-4 ${karigarJobs.loading ? "animate-spin" : ""}`}
            />
          </Button>

          {pendingCount > 0 && canAssignKarigar && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => setShowBulkAssignModal(true)}
              disabled={karigarJobs.loading}
            >
              <LightningBoltIcon className="w-4 h-4 mr-1" />
              Bulk Assign ({pendingCount})
            </Button>
          )}

          {assignedCount > 0 && !allKarigarWorkComplete && (
            <Button
              size="sm"
              variant="warning"
              onClick={() => setShowForceCompleteModal(true)}
              disabled={karigarJobs.loading}
            >
              <CheckCircleIcon className="w-4 h-4 mr-1" />
              Force Complete
            </Button>
          )}
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <UserGroupIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-900">
                Karigar Work Summary
              </h3>
              <p className="text-sm text-amber-700">
                Manufacturing status for this order
              </p>
            </div>
          </div>
          <Badge
            variant={
              allKarigarWorkComplete
                ? "green"
                : allItemsHaveKarigar
                ? "blue"
                : "yellow"
            }
            size="lg"
          >
            {allKarigarWorkComplete
              ? "All Complete"
              : allItemsHaveKarigar
              ? "In Progress"
              : "Needs Assignment"}
          </Badge>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg p-3 border border-amber-200">
            <p className="text-xs text-gray-600 mb-1">Total Items</p>
            <p className="text-xl font-bold text-gray-900">{totalItems}</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-green-200">
            <p className="text-xs text-gray-600 mb-1">Assigned</p>
            <p className="text-xl font-bold text-green-600">{assignedCount}</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <p className="text-xs text-gray-600 mb-1">In Progress</p>
            <p className="text-xl font-bold text-blue-600">{inProgressCount}</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-emerald-200">
            <p className="text-xs text-gray-600 mb-1">Completed</p>
            <p className="text-xl font-bold text-emerald-600">
              {completedCount}
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-amber-200">
            <p className="text-xs text-gray-600 mb-1">Pending</p>
            <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
          </div>
        </div>

        {/* Progress Bar */}
        {totalItems > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>Overall Progress</span>
              <span>{Math.round((completedCount / totalItems) * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${(completedCount / totalItems) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* View Content */}
      {activeView === "assignments" && (
        <AssignmentsView
          order={order}
          itemsWithoutKarigar={itemsWithoutKarigar}
          itemsWithKarigarStatus={itemsWithKarigarStatus}
          karigarAssignments={karigarAssignments}
          karigarJobs={karigarJobs}
          canAssignKarigar={canAssignKarigar}
          onAssignKarigar={onAssignKarigar}
          onViewQueue={onViewQueue}
          onUpdateJob={handleUpdateJobStatus}
          onCancelJob={handleCancelJob}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusConfig={getStatusConfig}
          loading={isLoading}
        />
      )}

      {activeView === "summary" && (
        <SummaryView
          order={order}
          summary={karigarJobs.summary}
          jobs={karigarJobs.list}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusConfig={getStatusConfig}
          loading={karigarJobs.loading}
        />
      )}

      {activeView === "readiness" && (
        <ReadinessView
          order={order}
          readiness={karigarJobs.readiness}
          onForceComplete={() => setShowForceCompleteModal(true)}
          formatDate={formatDate}
          getStatusConfig={getStatusConfig}
          loading={karigarJobs.loading}
        />
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <BulkAssignModal
          order={order}
          itemsWithoutKarigar={itemsWithoutKarigar}
          onClose={() => setShowBulkAssignModal(false)}
          onSubmit={handleBulkCreateJobs}
          loading={karigarJobs.loading}
        />
      )}

      {/* Force Complete Modal */}
      {showForceCompleteModal && (
        <ForceCompleteModal
          order={order}
          pendingJobs={
            karigarJobs.list?.filter(
              (j) => !["COMPLETED", "DELIVERED", "CANCELLED"].includes(j.status)
            ) || []
          }
          onClose={() => setShowForceCompleteModal(false)}
          onSubmit={handleForceCompleteJobs}
          loading={karigarJobs.loading}
        />
      )}
    </div>
  );
};

// ============================================================================
// ASSIGNMENTS VIEW
// ============================================================================
const AssignmentsView = ({
  order,
  itemsWithoutKarigar,
  itemsWithKarigarStatus,
  karigarAssignments,
  karigarJobs,
  canAssignKarigar,
  onAssignKarigar,
  onViewQueue,
  onUpdateJob,
  onCancelJob,
  formatCurrency,
  formatDate,
  getStatusConfig,
  loading,
}) => {
  // Get jobs list from karigarJobs Redux state
  const jobsList = karigarJobs?.list || [];
  const itemJobMapping = karigarJobs?.itemJobMapping || [];

  return (
    <div className="space-y-6">
      {/* Items Needing Karigar */}
      {itemsWithoutKarigar?.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <h4 className="font-semibold text-amber-900 flex items-center gap-2">
              <ExclamationCircleIcon className="w-5 h-5" />
              Items Needing Karigar Assignment ({itemsWithoutKarigar.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-200">
            {itemsWithoutKarigar.map((item) => (
              <div
                key={item.index}
                className="p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {item.productSnapshot?.name ||
                      item.product?.name ||
                      `Item #${item.index + 1}`}
                  </p>
                  <p className="text-sm text-gray-600">
                    SKU: {item.productSnapshot?.sku || "-"} • Qty:{" "}
                    {item.quantity || 1}
                  </p>
                  {item.customization && (
                    <p className="text-xs text-gray-500 mt-1">
                      Customization:{" "}
                      {Object.entries(item.customization)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ")}
                    </p>
                  )}
                </div>
                {canAssignKarigar && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      onAssignKarigar(
                        item.index,
                        item.product?._id || item.productSnapshot?._id
                      )
                    }
                    className="flex items-center gap-1"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Assign Karigar
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Karigar Assignments */}
      {karigarAssignments?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <BriefcaseIcon className="w-5 h-5 text-primary" />
              Karigar Assignments ({karigarAssignments.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-200">
            {karigarAssignments.map((assignment, idx) => {
              const item = order?.items?.[assignment.orderItemIndex];
              const statusConfig = getStatusConfig(assignment.status);
              const StatusIcon = statusConfig.icon;

              // Find matching job from jobsData
              const matchingJob = jobsList.find(
                (j) =>
                  j.orderItemIndex === assignment.orderItemIndex ||
                  j.jobId === assignment.jobId
              );

              return (
                <div key={assignment.jobId || idx} className="p-4">
                  {/* Item Info */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {assignment.productName ||
                          item?.productSnapshot?.name ||
                          `Item #${assignment.orderItemIndex + 1}`}
                      </p>
                      <p className="text-sm text-gray-600">
                        SKU: {assignment.productSku || "-"} • Job:{" "}
                        {assignment.jobNumber || "-"}
                      </p>
                    </div>
                    <Badge variant={statusConfig.color}>
                      <StatusIcon className="w-3 h-3 mr-1 inline" />
                      {statusConfig.label}
                    </Badge>
                  </div>

                  {/* Karigar Details */}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {assignment.karigarName || "Unknown Karigar"}
                          </p>
                          <p className="text-xs text-gray-600">
                            Code: {assignment.karigarCode || "-"} • Phone:{" "}
                            {assignment.karigarPhone || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onViewQueue(assignment.karigarId)}
                          title="View Queue"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Button>
                        {!["COMPLETED", "DELIVERED", "CANCELLED"].includes(
                          assignment.status
                        ) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              onCancelJob(
                                assignment.jobId,
                                "Cancelled from order"
                              )
                            }
                            className="text-red-600 hover:bg-red-50"
                            title="Cancel Job"
                          >
                            <XCircleIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500">Assigned</p>
                        <p className="font-medium text-gray-900">
                          {assignment.timeline?.assignedAt
                            ? formatDate(assignment.timeline.assignedAt)
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Expected</p>
                        <p className="font-medium text-gray-900">
                          {assignment.timeline?.expectedCompletionDate
                            ? formatDate(
                                assignment.timeline.expectedCompletionDate
                              )
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Work Type</p>
                        <p className="font-medium text-gray-900">
                          {assignment.workType || "NEW_MAKING"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Priority</p>
                        <Badge
                          variant={
                            assignment.priority === "URGENT" ||
                            assignment.priority === "CRITICAL"
                              ? "red"
                              : assignment.priority === "HIGH"
                              ? "orange"
                              : "gray"
                          }
                          size="sm"
                        >
                          {assignment.priority || "NORMAL"}
                        </Badge>
                      </div>
                    </div>

                    {/* Quality Check */}
                    {assignment.qualityCheck?.checkedAt && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              assignment.qualityCheck.passed ? "green" : "red"
                            }
                          >
                            QC:{" "}
                            {assignment.qualityCheck.passed
                              ? "Passed"
                              : "Failed"}
                          </Badge>
                          {assignment.qualityCheck.rating && (
                            <Badge variant="blue">
                              {assignment.qualityCheck.rating}
                            </Badge>
                          )}
                        </div>
                        {assignment.qualityCheck.notes && (
                          <p className="text-xs text-gray-600 mt-1">
                            {assignment.qualityCheck.notes}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Material Info */}
                    {assignment.materialRequired && (
                      <div className="mt-3 pt-3 border-t border-gray-200 flex gap-4 text-xs">
                        {assignment.materialRequired.goldWeight > 0 && (
                          <span className="text-amber-600">
                            Gold: {assignment.materialRequired.goldWeight}g
                          </span>
                        )}
                        {assignment.materialRequired.silverWeight > 0 && (
                          <span className="text-gray-600">
                            Silver: {assignment.materialRequired.silverWeight}g
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!itemsWithoutKarigar?.length && !karigarAssignments?.length && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No karigar assignments for this order</p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SUMMARY VIEW
// ============================================================================
const SummaryView = ({
  order,
  summary,
  jobs,
  formatCurrency,
  formatDate,
  getStatusConfig,
  loading,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">Loading summary...</span>
      </div>
    );
  }

  // Build summary from jobs if not provided
  const effectiveSummary = summary || buildSummaryFromJobs(jobs);

  if (!effectiveSummary) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <ChartBarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No summary data available</p>
        <p className="text-sm text-gray-400 mt-1">Click "Summary" to load</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* By Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-semibold text-gray-900 mb-4">Jobs by Status</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(effectiveSummary.byStatus || {}).map(
            ([status, count]) => {
              const config = getStatusConfig(status);
              return (
                <div key={status} className="p-3 bg-gray-50 rounded-lg">
                  <Badge variant={config.color} size="sm" className="mb-1">
                    {config.label}
                  </Badge>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* By Karigar */}
      {effectiveSummary.byKarigar &&
        Object.keys(effectiveSummary.byKarigar).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-900 mb-4">
              Jobs by Karigar
            </h4>
            <div className="space-y-2">
              {Object.entries(effectiveSummary.byKarigar).map(
                ([karigarName, data]) => (
                  <div
                    key={karigarName}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {karigarName}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">
                        {data.completed || 0}/{data.count || data.total || 0}{" "}
                        completed
                      </span>
                      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500"
                          style={{
                            width: `${
                              ((data.completed || 0) /
                                (data.count || data.total || 1)) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}

      {/* Material Totals */}
      {effectiveSummary.totalMaterialIssued && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-4">Material Issued</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <p className="text-xs text-amber-700 mb-1">Gold</p>
              <p className="text-xl font-bold text-amber-900">
                {effectiveSummary.totalMaterialIssued.gold || 0}g
              </p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Silver</p>
              <p className="text-xl font-bold text-gray-900">
                {effectiveSummary.totalMaterialIssued.silver || 0}g
              </p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Platinum</p>
              <p className="text-xl font-bold text-slate-900">
                {effectiveSummary.totalMaterialIssued.platinum || 0}g
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overdue Alert */}
      {effectiveSummary.overdueCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <ExclamationCircleIcon className="w-5 h-5" />
            <span className="font-semibold">
              {effectiveSummary.overdueCount} Overdue Jobs
            </span>
          </div>
          <p className="text-sm text-red-700 mt-1">
            These jobs have passed their expected completion date
          </p>
        </div>
      )}
    </div>
  );
};

// Helper function to build summary from jobs array
const buildSummaryFromJobs = (jobs) => {
  if (!jobs || jobs.length === 0) return null;

  const byStatus = {};
  const byKarigar = {};
  let overdueCount = 0;
  const totalMaterialIssued = { gold: 0, silver: 0, platinum: 0 };

  jobs.forEach((job) => {
    // Count by status
    const status = job.status || "PENDING";
    byStatus[status] = (byStatus[status] || 0) + 1;

    // Count by karigar
    const karigarName = job.karigarName || "Unassigned";
    if (!byKarigar[karigarName]) {
      byKarigar[karigarName] = { count: 0, completed: 0, total: 0 };
    }
    byKarigar[karigarName].count++;
    byKarigar[karigarName].total++;
    if (["COMPLETED", "DELIVERED"].includes(status)) {
      byKarigar[karigarName].completed++;
    }

    // Count overdue
    if (
      job.isOverdue ||
      (job.expectedCompletionDate &&
        new Date(job.expectedCompletionDate) < new Date() &&
        !["COMPLETED", "DELIVERED", "CANCELLED"].includes(status))
    ) {
      overdueCount++;
    }

    // Sum materials
    if (job.materialSummary) {
      totalMaterialIssued.gold += job.materialSummary.totalGoldIssued || 0;
      totalMaterialIssued.silver += job.materialSummary.totalSilverIssued || 0;
      totalMaterialIssued.platinum +=
        job.materialSummary.totalPlatinumIssued || 0;
    }
  });

  return {
    byStatus,
    byKarigar,
    overdueCount,
    totalMaterialIssued,
    totalJobs: jobs.length,
  };
};

// ============================================================================
// READINESS VIEW
// ============================================================================
const ReadinessView = ({
  order,
  readiness,
  onForceComplete,
  formatDate,
  getStatusConfig,
  loading,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">Checking readiness...</span>
      </div>
    );
  }

  // Use readiness data or derive from order
  const effectiveReadiness = readiness || deriveReadinessFromOrder(order);

  if (!effectiveReadiness) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <TruckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No readiness data available</p>
        <p className="text-sm text-gray-400 mt-1">Click "Readiness" to check</p>
      </div>
    );
  }

  const canFulfill = effectiveReadiness.canFulfill;
  const pendingJobs = effectiveReadiness.pendingJobs || [];
  const blockers = effectiveReadiness.blockers || [];

  return (
    <div className="space-y-6">
      {/* Readiness Status */}
      <div
        className={`rounded-lg p-6 border-2 ${
          canFulfill
            ? "bg-green-50 border-green-200"
            : "bg-amber-50 border-amber-200"
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          {canFulfill ? (
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
          ) : (
            <ExclamationCircleIcon className="w-8 h-8 text-amber-600" />
          )}
          <div>
            <h3
              className={`text-lg font-semibold ${
                canFulfill ? "text-green-900" : "text-amber-900"
              }`}
            >
              {canFulfill
                ? "Ready for Fulfillment"
                : "Not Ready for Fulfillment"}
            </h3>
            <p
              className={`text-sm ${
                canFulfill ? "text-green-700" : "text-amber-700"
              }`}
            >
              {effectiveReadiness.message ||
                (canFulfill
                  ? "All karigar work is complete"
                  : "Some jobs are still pending")}
            </p>
          </div>
        </div>

        {/* Stats */}
        {effectiveReadiness.stats && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="bg-white/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-gray-900">
                {effectiveReadiness.stats.totalJobs || 0}
              </p>
              <p className="text-xs text-gray-600">Total Jobs</p>
            </div>
            <div className="bg-white/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-green-600">
                {effectiveReadiness.stats.completedJobs || 0}
              </p>
              <p className="text-xs text-gray-600">Completed</p>
            </div>
            <div className="bg-white/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-blue-600">
                {effectiveReadiness.stats.activeJobs || 0}
              </p>
              <p className="text-xs text-gray-600">Active</p>
            </div>
            <div className="bg-white/50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-amber-600">
                {effectiveReadiness.stats.pendingJobs || 0}
              </p>
              <p className="text-xs text-gray-600">Pending</p>
            </div>
          </div>
        )}

        {!canFulfill && (
          <div className="flex gap-2 mt-4">
            <Button
              variant="warning"
              onClick={onForceComplete}
              className="flex items-center gap-2"
            >
              <LightningBoltIcon className="w-4 h-4" />
              Force Complete All Jobs
            </Button>
          </div>
        )}
      </div>

      {/* Blockers */}
      {blockers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
            <XCircleIcon className="w-5 h-5" />
            Blockers ({blockers.length})
          </h4>
          <ul className="space-y-2">
            {blockers.map((blocker, idx) => (
              <li
                key={idx}
                className="text-sm text-red-700 flex items-start gap-2"
              >
                <span className="text-red-500 mt-0.5">•</span>
                {blocker.message || blocker}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pending Jobs List */}
      {pendingJobs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900">
              Pending Jobs ({pendingJobs.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingJobs.map((job, idx) => {
              const config = getStatusConfig(job.status);
              return (
                <div
                  key={job.jobNumber || idx}
                  className="p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {job.productName || job.title || "Unknown"}
                    </p>
                    <p className="text-sm text-gray-600">
                      Job: {job.jobNumber} • Karigar:{" "}
                      {job.karigarName || "Unassigned"}
                    </p>
                  </div>
                  <Badge variant={config.color}>{config.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to derive readiness from order
const deriveReadinessFromOrder = (order) => {
  if (!order) return null;

  const karigarWorkStatus = order.karigarWorkStatus || {};
  const assignments = order.karigarAssignments || [];

  const pendingJobs = assignments.filter(
    (a) => !["COMPLETED", "DELIVERED", "CANCELLED"].includes(a.status)
  );

  const canFulfill = karigarWorkStatus.allCompleted || pendingJobs.length === 0;

  return {
    canFulfill,
    message: canFulfill
      ? "All karigar work is complete"
      : `${pendingJobs.length} job(s) still pending`,
    stats: {
      totalJobs: assignments.length,
      completedJobs: karigarWorkStatus.completedItems || 0,
      activeJobs: karigarWorkStatus.inProgressItems || 0,
      pendingJobs: pendingJobs.length,
    },
    pendingJobs: pendingJobs.map((a) => ({
      jobNumber: a.jobNumber,
      productName: a.productName,
      karigarName: a.karigarName,
      status: a.status,
    })),
    blockers: !canFulfill
      ? [{ message: `${pendingJobs.length} karigar job(s) not completed` }]
      : [],
  };
};

// ============================================================================
// BULK ASSIGN MODAL
// ============================================================================
const BulkAssignModal = ({
  order,
  itemsWithoutKarigar,
  onClose,
  onSubmit,
  loading,
}) => {
  const [defaultKarigarId, setDefaultKarigarId] = useState("");
  const [defaultPriority, setDefaultPriority] = useState("NORMAL");
  const [expectedDate, setExpectedDate] = useState(
    order?.expectedDeliveryDate
      ? new Date(order.expectedDeliveryDate).toISOString().split("T")[0]
      : ""
  );

  const handleSubmit = () => {
    onSubmit({
      defaultJobType: "CUSTOM_ORDER",
      defaultWorkType: "NEW_MAKING",
      defaultPriority,
      expectedCompletionDate: expectedDate || undefined,
      defaultKarigarId: defaultKarigarId || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Bulk Create Karigar Jobs
          </h3>
          <p className="text-sm text-gray-600">
            Create jobs for {itemsWithoutKarigar?.length || 0} items
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={defaultPriority}
              onChange={(e) => setDefaultPriority(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="LOW">Low</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expected Completion Date
            </label>
            <input
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1"
          >
            {loading ? "Creating..." : "Create Jobs"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// FORCE COMPLETE MODAL
// ============================================================================
const ForceCompleteModal = ({
  order,
  pendingJobs,
  onClose,
  onSubmit,
  loading,
}) => {
  const [qualityRating, setQualityRating] = useState("GOOD");
  const [qualityNotes, setQualityNotes] = useState("");
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    onSubmit({
      qualityRating,
      qualityNotes: qualityNotes || undefined,
      reason: reason || "Force completed from order",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200 bg-amber-50">
          <h3 className="text-lg font-semibold text-amber-900">
            Force Complete All Jobs
          </h3>
          <p className="text-sm text-amber-700">
            This will complete {pendingJobs?.length || 0} pending jobs
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <ExclamationCircleIcon className="w-4 h-4 inline mr-1" />
              Jobs in early stages (PENDING, ASSIGNED) will be marked with a
              warning flag.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quality Rating
            </label>
            <select
              value={qualityRating}
              onChange={(e) => setQualityRating(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="EXCELLENT">Excellent</option>
              <option value="GOOD">Good</option>
              <option value="SATISFACTORY">Satisfactory</option>
              <option value="NEEDS_IMPROVEMENT">Needs Improvement</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quality Notes (Optional)
            </label>
            <textarea
              value={qualityNotes}
              onChange={(e) => setQualityNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Any notes about quality..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Force Complete
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Customer pickup scheduled"
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1"
          >
            {loading ? "Completing..." : "Force Complete"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KarigarTab;
